import type { MouseEvent } from 'react';
import { useCallback, useRef, useState } from 'react';

import { globalRequire } from '@confluence/wrm/entry-points/globalRequire.ts';
import { requireLegacyWRM } from '@confluence/wrm/entry-points/requireLegacyWRMHelpers.ts';

// Migrated macros do not load the legacy preview plugin as a side effect of rendering.
// Load it on interaction and open the selected file explicitly, without replaying the click.
export const useLegacyAttachmentPreview = (): {
	onPreviewClick: (event: MouseEvent<HTMLAnchorElement>) => void;
	error: Error | undefined;
} => {
	const [error, setError] = useState<Error>();
	const pending = useRef(false);

	const onPreviewClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
		if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0) {
			return;
		}
		event.preventDefault();
		// The plugin may already be loaded by another macro. Avoid opening it twice.
		event.stopPropagation();
		if (pending.current) {
			return;
		}
		const target = event.currentTarget;
		pending.current = true;
		setError(undefined);
		const onFailure = () => {
			pending.current = false;
			if (target.isConnected) {
				setError(new Error('Unable to load the attachments macro preview'));
			}
		};
		requireLegacyWRM(
			['wr!com.atlassian.confluence.plugins.confluence-previews:confluence-previews-jquery'],
			() => {
				if (!target.isConnected) {
					pending.current = false;
					return;
				}
				try {
					const preview: {
						loadConfluencePreviews: (element: HTMLElement) => PromiseLike<unknown>;
					} = globalRequire('cp/confluence/preview');
					void Promise.resolve(preview.loadConfluencePreviews(target))
						.catch(onFailure)
						.finally(() => {
							pending.current = false;
						});
				} catch {
					onFailure();
				}
			},
			onFailure,
		);
	}, []);

	return { onPreviewClick, error };
};

// Strip ARI wrapper → bare numeric id the legacy previewer expects.
export const toPreviewAttachmentId = (attachmentId: string): string => {
	if (!attachmentId) {
		return attachmentId;
	}
	const tail = attachmentId.includes('/')
		? attachmentId.slice(attachmentId.lastIndexOf('/') + 1)
		: attachmentId;
	return /^att\d+$/.test(tail) ? tail.slice(3) : tail;
};

export type LegacyPreviewTarget = {
	pageId: string;
	attachmentId: string;
	fileName: string;
};

export const buildLegacyPreviewParam = ({
	pageId,
	attachmentId,
	fileName,
}: LegacyPreviewTarget): string => `/${pageId}/${toPreviewAttachmentId(attachmentId)}/${fileName}`;
